import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DashboardLayout from "@/components/DashboardLayout";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Package } from "lucide-react";
import PdfDownloadButton from "@/components/PdfDownloadButton";

const STAGES = [
  { key: "material_ordered", label: "تم طلب المواد", pct: 15 },
  { key: "material_received", label: "تم استلام المواد", pct: 25 },
  { key: "manufacturing_started", label: "بدأ التصنيع", pct: 35 },
  { key: "progress_25", label: "25% مكتمل", pct: 40 },
  { key: "progress_50", label: "50% مكتمل", pct: 55 },
  { key: "progress_75", label: "75% مكتمل", pct: 75 },
  { key: "manufacturing_completed", label: "التصنيع مكتمل", pct: 85 },
  { key: "internal_inspection", label: "الفحص الداخلي مكتمل", pct: 90 },
  { key: "ready_for_inspection", label: "جاهز للفحص من MERQAV", pct: 95 },
];

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const submitUpdate = async () => {
    const info = STAGES.find((s) => s.key === stage);
    if (!info) return;
    try {
      await api.post(`/orders/${id}/updates`, { stage, note, progress_percentage: info.pct });
      toast.success("تم إضافة تحديث الإنتاج");
      setStage(""); setNote(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <DashboardLayout><Loader2 className="w-8 h-8 animate-spin text-brand-amber m-8" /></DashboardLayout>;
  if (!order) return null;

  return (
    <DashboardLayout>
      <div className="mb-6">
        <div className="text-xs font-bold tracking-widest text-brand-amber mb-2">طلبية</div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-3xl font-bold text-brand-navy">{order.order_number}</h1>
          <StatusBadge status={order.status} />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Package className="w-5 h-5 text-brand-amber" />
              <h3 className="font-bold text-brand-navy">تقدم التصنيع</h3>
            </div>
            <div className="flex items-center gap-3 mb-2">
              <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-brand-amber transition-all duration-500" style={{ width: `${order.progress_percentage || 0}%` }} />
              </div>
              <span className="font-bold text-brand-navy">{order.progress_percentage || 0}%</span>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="production-timeline">
            <h3 className="font-bold text-brand-navy mb-4">سجل التحديثات</h3>
            <div className="space-y-3">
              {(order.production_updates || []).map((u, i) => (
                <div key={`${u.at || ""}-${u.stage || ""}-${i}`} className="flex gap-3">
                  <div className="w-2 h-2 rounded-full bg-brand-amber mt-2 shrink-0" />
                  <div>
                    <div className="text-sm font-semibold text-brand-navy">{STAGES.find((s) => s.key === u.stage)?.label || u.stage}</div>
                    {u.note && <div className="text-sm text-slate-600">{u.note}</div>}
                    <div className="text-xs text-slate-400">{u.at?.slice(0, 16).replace("T", " ")} {u.by ? `• ${u.by}` : ""}</div>
                  </div>
                </div>
              ))}
              {(order.production_updates || []).length === 0 && <div className="text-center text-slate-500 text-sm py-4">لم يتم إضافة تحديثات بعد.</div>}
            </div>
          </div>

          {(user.role === "workshop_partner" || user.role === "admin" || user.role === "operations_employee") && order.status !== "completed" && (
            <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="add-update-form">
              <h3 className="font-bold text-brand-navy mb-4">إضافة تحديث إنتاج</h3>
              <div className="space-y-3">
                <div>
                  <Label>المرحلة</Label>
                  <Select value={stage} onValueChange={setStage}>
                    <SelectTrigger className="mt-1.5" data-testid="update-stage-select"><SelectValue placeholder="اختر المرحلة" /></SelectTrigger>
                    <SelectContent>{STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>ملاحظة (اختياري)</Label>
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" data-testid="update-note" />
                </div>
                <Button className="btn-cta" disabled={!stage} onClick={submitUpdate} data-testid="submit-update-btn">إضافة التحديث</Button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <h3 className="font-bold text-brand-navy mb-3">معلومات الطلبية</h3>
            <div className="text-sm space-y-2 mb-4">
              <div><span className="text-slate-500">التاريخ:</span> {order.created_at?.slice(0, 10)}</div>
              {user.role !== "workshop_partner" && order.total_price && <div><span className="text-slate-500">الإجمالي:</span> <span className="font-bold text-brand-navy">{order.total_price?.toLocaleString()} ر.س</span></div>}
              {user.role === "admin" && order.workshop_cost && <div><span className="text-slate-500">تكلفة الورشة:</span> {order.workshop_cost?.toLocaleString()} ر.س</div>}
              {user.role === "admin" && order.workshop_cost && order.total_price && <div><span className="text-slate-500">الربح:</span> <span className="text-emerald-700 font-bold">{(order.total_price - order.workshop_cost).toLocaleString()} ر.س</span></div>}
            </div>
            {user.role !== "workshop_partner" && (
              <PdfDownloadButton
                endpoint={`/orders/${order.id}/invoice-pdf`}
                filename={`merqav-invoice-${order.order_number}.pdf`}
                label="تحميل الفاتورة PDF"
                className="w-full"
                data-testid="download-invoice-pdf-btn"
              />
            )}
          </div>
          {order.request && (
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <h3 className="font-bold text-brand-navy mb-3">القطعة</h3>
              <div className="text-sm space-y-2">
                <div><span className="text-slate-500">الاسم:</span> {order.request.part_name}</div>
                <div><span className="text-slate-500">الكمية:</span> {order.request.quantity}</div>
                <div><span className="text-slate-500">المادة:</span> {order.request.material || "—"}</div>
                <div><span className="text-slate-500">الأبعاد:</span> {order.request.dimensions || "—"}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
